import { useState } from 'react';
import { AlertCircle, IndianRupee } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DrawerSection } from '@/components/drawer/DrawerSection';
import { FieldError } from '@/components/form/FieldError';
import { CASH_PAYMENT_TYPES, PaymentTypeField } from '@/components/form/PaymentTypeField';
import { StatusBadge } from '@/components/StatusBadge';
import { getApiErrorMessage } from '@/api/errorHandler';
import { BalancePaymentRequestMapper } from '@/api/mapper/balance';
import { createBalancePayment } from '@/api/service/balance';
import type { CashPaymentType } from '@/api/type/payment';
import type { MemberBalance } from '@/types';
import { formatCurrency } from '@/lib/payments';
import { getBalancePaymentError, splitBalancePayment } from '@/lib/balances';
import { FIELD_CLASS, LABEL_CLASS, PRIMARY_BUTTON_CLASS } from '@/lib/styles';

interface BalancePaymentFormProps {
  balance: MemberBalance;
  onSaved: () => void;
}

/** Records money the member pays back: any amount up to the balance due, Cash or Online. */
export function BalancePaymentForm({ balance, onSaved }: BalancePaymentFormProps) {
  const [amount, setAmount] = useState('');
  const [paymentType, setPaymentType] = useState<CashPaymentType>('Cash');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  const error = getBalancePaymentError(amount, balance.balanceDue);
  const value = Number(amount);
  const split = error ? null : splitBalancePayment(value, balance.interestDue);
  const balanceAfter = balance.balanceDue - value;

  const handleSubmit = async () => {
    setTouched(true);
    if (error) return;
    setSubmitting(true);
    setServerError('');
    try {
      await createBalancePayment(Number(balance.memberId), BalancePaymentRequestMapper({ amount: value, paymentType }));
      onSaved();
    } catch (submitError) {
      setServerError(getApiErrorMessage(submitError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DrawerSection icon={IndianRupee} title="Balance Payment" description="Member can pay any amount, any time">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="balance-amount" className={LABEL_CLASS}>
            Amount (₹) *
          </Label>
          <Button
            type="button"
            variant="link"
            className="h-auto p-0 text-xs text-[#4369a0]"
            onClick={() => setAmount(String(balance.balanceDue))}
          >
            Pay Full {formatCurrency(balance.balanceDue)}
          </Button>
        </div>
        <Input
          id="balance-amount"
          inputMode="numeric"
          placeholder={`Max ${formatCurrency(balance.balanceDue)}`}
          value={amount}
          onChange={(event) => setAmount(event.target.value.replace(/\D/g, '').slice(0, 7))}
          onBlur={() => setTouched(true)}
          className={FIELD_CLASS}
        />
        <FieldError show={touched && Boolean(error)} message={error} />
      </div>

      <PaymentTypeField<CashPaymentType>
        id="balance-payment-type"
        options={CASH_PAYMENT_TYPES}
        value={paymentType}
        onChange={setPaymentType}
      />

      <dl className="divide-y divide-gray-100 border-t border-gray-100">
        {[
          ['Goes to Interest', split ? formatCurrency(split.toInterest) : '--'],
          ['Goes to Balance', split ? formatCurrency(split.toPrincipal) : '--'],
        ].map(([label, text]) => (
          <div key={label} className="flex items-center justify-between gap-6 py-3">
            <dt className="text-sm text-gray-500">{label}</dt>
            <dd className="text-sm tabular-nums text-gray-900">{text}</dd>
          </div>
        ))}
        <div className="flex items-center justify-between gap-6 py-3">
          <dt className="text-sm text-gray-500">Balance After Payment</dt>
          <dd className="text-base font-semibold tabular-nums text-amber-900">
            {!split ? '--' : balanceAfter === 0 ? <StatusBadge status="no_dues" /> : formatCurrency(balanceAfter)}
          </dd>
        </div>
      </dl>

      {serverError && (
        <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{serverError}</span>
        </div>
      )}

      <div className="flex justify-end">
        <Button type="button" onClick={handleSubmit} disabled={submitting} className={`min-w-36 ${PRIMARY_BUTTON_CLASS}`}>
          {submitting ? 'Saving...' : 'Add Balance Payment'}
        </Button>
      </div>
    </DrawerSection>
  );
}
